/**  
  Developers: Sakshi Bhise  
  File: AuthContext.tsx  
  Date: 06/18/2025  
  Project Name: AskHR  
  Team: DSET   
 *  
 * Purpose:  
 *  - Defines a global React Context for authentication state (token and status).  
 *  - Enables any child component to access or react to authentication status via `useAuth()` hook.  
 *  - Standardizes authentication state management for consistency and scalability across the app.  
 * 
 */  
  
// ===================== Imports ===================== //  
// Import React and context hooks for global state management    
import React, { createContext, useContext } from "react";  
  
// ===================== Type Definitions ===================== //  
/**  
 * AuthContextType  
 * - Describes the shape of authentication context data.  
 * - 'token': The authentication token for the current session (null if not authenticated).
 * - eg: token:"weah12334jfkjdhfnnc"  
 */  
type AuthContextType = {  
  token: string | null;  
};  
  
// ===================== AuthContext Creation ===================== //  
/**  
 * AuthContext  
 * - Global React Context for authentication state.  
 * - Provides 'token' (string or null) and 'isAuthenticated' (boolean).  
 * - Default value: not authenticated, no token.  
 * - eg: {token:"weah12334jfkjdhfnnc" , isAuthenticated:false}  
 */  
export const AuthContext = React.createContext<{  
  token: string | null;  
  isAuthenticated: boolean;  
}>({  
  token: null,  
  isAuthenticated: false  
});  
  
// ===================== Custom Hook ===================== //  
/**  
 * useAuth  
 * - Convenience hook to access AuthContext in any component.  
 * - Returns the current authentication context value.  
 */  
export function useAuth() {  
  return useContext(AuthContext);  
}